import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { CapabilityKey } from '@/hooks/usePropertyCapabilities';
import {
  useCreateServicePlan,
  useUpdateServicePlan,
  type ServicePlanWithCaps,
} from '@/hooks/useServicePlans';

// ─── Capability groups for checklist ────────────────────────────────────────

interface CapabilityGroup {
  label: string;
  capabilities: { key: CapabilityKey; label: string }[];
}

const CAPABILITY_GROUPS: CapabilityGroup[] = [
  {
    label: 'Monitoring & Forecasting',
    capabilities: [
      { key: 'monitoring', label: 'Monitoring' },
      { key: 'forecast_basic', label: 'Basic Forecast' },
      { key: 'forecast_extended', label: 'Extended Forecast' },
    ],
  },
  {
    label: 'Service Operations',
    capabilities: [
      { key: 'notifications', label: 'Notifications' },
      { key: 'issue_queue', label: 'Issue Queue (operator)' },
      { key: 'issue_submission', label: 'Issue Submission (homeowner)' },
      { key: 'service_history', label: 'Service History' },
    ],
  },
  {
    label: 'Warranty & Reports',
    capabilities: [
      { key: 'warranty_registry', label: 'Warranty Registry' },
      { key: 'warranty_claim_packets', label: 'Warranty Claim Packets' },
      { key: 'customer_reports', label: 'Customer Reports' },
    ],
  },
  {
    label: 'Growth & Opportunities',
    capabilities: [
      { key: 'upgrade_opportunities_operator', label: 'Upgrade Opportunities (operator)' },
      { key: 'upgrade_opportunities_homeowner', label: 'Upgrade Opportunities (homeowner)' },
    ],
  },
  {
    label: 'Portal & Access',
    capabilities: [
      { key: 'homeowner_portal', label: 'Homeowner Portal' },
      { key: 'fleet_insights', label: 'Fleet Insights' },
      { key: 'gateway_management', label: 'Gateway Management' },
    ],
  },
  {
    label: 'Device Control',
    capabilities: [
      { key: 'control_read', label: 'Control Read' },
      { key: 'control_write', label: 'Control Write' },
    ],
  },
];

// Tier capability matrix (mirrors migration seed)
const TIER_CAPABILITIES: Record<string, Set<string>> = {
  monitor: new Set(['monitoring', 'forecast_basic']),
  alert: new Set([
    'monitoring', 'forecast_basic', 'forecast_extended',
    'notifications', 'fleet_insights', 'homeowner_portal',
    'issue_queue', 'issue_submission', 'service_history',
    'warranty_registry', 'customer_reports',
    'upgrade_opportunities_operator', 'upgrade_opportunities_homeowner',
    'gateway_management',
  ]),
  predict: new Set([
    'monitoring', 'forecast_basic', 'forecast_extended',
    'notifications', 'fleet_insights', 'homeowner_portal',
    'issue_queue', 'issue_submission', 'service_history',
    'warranty_registry', 'warranty_claim_packets', 'customer_reports',
    'upgrade_opportunities_operator', 'upgrade_opportunities_homeowner',
    'gateway_management', 'control_read', 'control_write',
  ]),
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan?: ServicePlanWithCaps | null;
  orgTier: string;
}

export function ServicePlanDialog({ open, onOpenChange, plan, orgTier }: Props) {
  const isEdit = !!plan;
  const createPlan = useCreateServicePlan();
  const updatePlan = useUpdateServicePlan();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceDollars, setPriceDollars] = useState('');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [isDefault, setIsDefault] = useState(false);
  const [selectedCaps, setSelectedCaps] = useState<Set<CapabilityKey>>(new Set());

  useEffect(() => {
    if (open) {
      if (plan) {
        setName(plan.name);
        setDescription(plan.description ?? '');
        setPriceDollars((plan.price_cents / 100).toFixed(2));
        setBillingInterval(plan.billing_interval);
        setIsDefault(plan.is_default);
        setSelectedCaps(new Set(plan.capabilities));
      } else {
        setName('');
        setDescription('');
        setPriceDollars('');
        setBillingInterval('monthly');
        setIsDefault(false);
        setSelectedCaps(new Set());
      }
    }
  }, [open, plan]);

  const tierCaps = TIER_CAPABILITIES[orgTier] ?? TIER_CAPABILITIES['predict'];

  const toggleCap = (cap: CapabilityKey) => {
    setSelectedCaps((prev) => {
      const next = new Set(prev);
      if (next.has(cap)) {
        next.delete(cap);
      } else {
        next.add(cap);
      }
      return next;
    });
  };

  const isPending = createPlan.isPending || updatePlan.isPending;

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Plan name is required');
      return;
    }
    const cents = Math.round(parseFloat(priceDollars || '0') * 100);
    if (isNaN(cents) || cents < 0) {
      toast.error('Price must be a valid number');
      return;
    }

    const capabilities = Array.from(selectedCaps);

    if (isEdit && plan) {
      updatePlan.mutate(
        {
          id: plan.id,
          name: trimmedName,
          description: description.trim() || null,
          price_cents: cents,
          billing_interval: billingInterval,
          is_default: isDefault,
          capabilities,
        },
        {
          onSuccess: () => {
            toast.success('Plan updated');
            onOpenChange(false);
          },
          onError: (err) => {
            toast.error(`Failed to update: ${err instanceof Error ? err.message : 'Unknown error'}`);
          },
        },
      );
    } else {
      createPlan.mutate(
        {
          name: trimmedName,
          description: description.trim() || undefined,
          price_cents: cents,
          billing_interval: billingInterval,
          is_default: isDefault,
          capabilities,
        },
        {
          onSuccess: () => {
            toast.success('Plan created');
            onOpenChange(false);
          },
          onError: (err) => {
            toast.error(`Failed to create: ${err instanceof Error ? err.message : 'Unknown error'}`);
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Service Plan' : 'Create Service Plan'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Plan Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Basic Monitoring"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description for this plan"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Billing Interval</Label>
              <Select value={billingInterval} onValueChange={(v) => setBillingInterval(v as 'monthly' | 'annual')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Default Plan</Label>
              <p className="text-xs text-muted-foreground">Auto-assign to new properties</p>
            </div>
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
          </div>

          <div className="space-y-3">
            <Label>Capabilities</Label>
            <p className="text-xs text-muted-foreground">
              Select which features this plan includes. Capabilities above your platform tier ({orgTier}) are disabled.
            </p>
            {CAPABILITY_GROUPS.map((group) => (
              <div key={group.label} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {group.label}
                </p>
                <div className="space-y-1 pl-1">
                  {group.capabilities.map(({ key, label }) => {
                    const disabledByTier = !tierCaps.has(key);
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-muted/50 ${
                          disabledByTier ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                      >
                        <Checkbox
                          checked={selectedCaps.has(key)}
                          onCheckedChange={() => toggleCap(key)}
                          disabled={disabledByTier}
                        />
                        <span className="text-sm">{label}</span>
                        {disabledByTier && (
                          <span className="text-[10px] text-muted-foreground ml-auto">
                            requires higher tier
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
